/* eslint-disable @typescript-eslint/naming-convention */
import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ShareManifestContextableTypes, ShareManifestPutResponse, ShareManifestServer } from '@api/v4/share_manifests/ShareManifestTypes';
import { StandardSwitch } from '@components/library/switch';
import Tooltip from '@components/common/tooltip/main';
import { localizeDateTime } from '@helpers/localize';
import { setStorage, SHARE_LINK_NOTIFICATIONS_PREFERENCE_KEY, StorageTypes } from '@helpers/storage';
import { Card, CardHeader, CardPairing } from '../card';
import { mapShareManifestServerToClient } from '../../helpers';

import classes from './styles/metadata.module.scss';

interface MetadataProps {
  assetsHidden: number;
  assetsShown: number;
  manifest: ShareManifestServer;
  requireShareIdentification: boolean;
  resourceName: string;
  resourcePath: string;
  resourceType: ShareManifestContextableTypes;
}

export const Metadata: FunctionComponent<MetadataProps> = (props) => {
  const { assetsHidden, assetsShown, manifest, requireShareIdentification, resourceName, resourcePath, resourceType } = props;

  const [shareManifestServer, setShareManifestServer] = useState(manifest);
  const [updated, setUpdated] = useState(false);

  const shareManifest = mapShareManifestServerToClient(shareManifestServer);
  const { availabilityEnd, createdBy, createdAt, expires, internal, key, notificationsOptin, requireIdentification } = shareManifest;

  const putShareManifest = useFetch<ShareManifestPutResponse>({
    body: {
      data: {
        attributes: {
          notifications_optin: notificationsOptin,
          require_identification: requireIdentification
        }
      }
    },
    fetchOnMount: false,
    method: 'PUT',
    url: `/api/v4/share_manifests/${key}`
  });

  useEffect(() => {
    if (putShareManifest.error) {
      Notify.create({
        title: bfTranslate('Error updating Share Link. Please try again.'),
        type: 'error'
      });
    }
  }, [putShareManifest.error]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (putShareManifest.response) {
      setShareManifestServer({ ...shareManifestServer, ...putShareManifest.response.data.attributes });
      Notify.create({
        title: bfTranslate('Share Link successfully updated.'),
        type: 'success'
      });
    }
  }, [putShareManifest.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updated) {
      putShareManifest.fetch();
      setUpdated(false);
    }
  }, [updated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card>
      <CardHeader>
        <CardPairing>
          <h6 className={classes.h6}>{resourceType}</h6>
          <p className={classnames(classes.p, classes.noMargin)}>
            <a href={resourcePath} rel="noopener noreferrer" target="_blank">{resourceName === 'Brandfolder' && BFG.getty_client === 'true' ? bfTranslate('Library') : resourceName}</a>
          </p>
        </CardPairing>
      </CardHeader>
      <CardPairing className={classes.p} tag="p">
        <div>
          <strong>{bfTranslate('Assets hidden')}</strong>
          <Tooltip
            className={classes.tooltip}
            tooltipContent={bfTranslate('Hidden assets are either unpublished, expired or unapproved.')}
            tooltipId="assets-hidden-metadata"
          >
            <span className={`bff-tool-tip ${classes['bff-tool-tip']}`} />
          </Tooltip>
        </div>
        <span>{assetsHidden}</span>
      </CardPairing>
      <CardPairing className={classes.p} tag="p">
        <strong>{bfTranslate('Assets shown')}</strong> <span>{assetsShown}</span>
      </CardPairing>
      <CardPairing className={classes.p} tag="p">
        <strong>{bfTranslate('Access')}</strong> <span>{internal ? bfTranslate('Private') : bfTranslate('Public')}</span>
      </CardPairing>
      {/* only show this switch if require_share_identification is turned off at the Brandfolder level (essentially a global override) */}
      {/* require_share_identification is toggle via the Brandfolder settings modal at "Require ID for public shares" */}
      {!requireShareIdentification && (
      <CardPairing>
        <p className={classnames(classes.p, classes.noMargin)}>
          <strong>{bfTranslate('Require email for access')}</strong>{' '}
        </p>
        <StandardSwitch
          className={classes.switch}
          id="share-manifest-require-identification-switch"
          isChecked={requireIdentification}
          onChange={(): void => {
            setShareManifestServer({
              ...shareManifestServer,
              require_identification: !shareManifestServer.require_identification
            });
            setUpdated(true);
          }}
        />
      </CardPairing>
      )}
      <CardPairing className={classes.p} tag="p">
        <strong>{bfTranslate('Creation date')}</strong> <span>{localizeDateTime(createdAt)}</span>
      </CardPairing>
      <CardPairing className={classes.p} tag="p">
        <strong>{bfTranslate('Expiration date')}</strong> <span>{expires ? localizeDateTime(availabilityEnd) : '-'}</span>
      </CardPairing>
      <CardPairing className={classes.p} tag="p">
        <strong>{bfTranslate('Created by')}</strong> <span>{createdBy}</span>
      </CardPairing>
      <CardPairing>
        <p className={classnames(classes.p, classes.noMargin)}>
          <strong>{bfTranslate('Notifications')}</strong>{' '}
        </p>
        <StandardSwitch
          className={classes.switch}
          id="share-manifest-notifications-switch"
          isChecked={notificationsOptin}
          onChange={(): void => {
            const updatedNotificationsOptin = !shareManifestServer.notifications_optin;
            setShareManifestServer({
              ...shareManifestServer,
              notifications_optin: updatedNotificationsOptin
            });
            setUpdated(true);
            setStorage(StorageTypes.Local, SHARE_LINK_NOTIFICATIONS_PREFERENCE_KEY, updatedNotificationsOptin);
          }}
        />
      </CardPairing>
    </Card>
  );
};
